import React, { useEffect, useState, useCallback } from 'react';
import { useI18n } from '../../contexts/I18nContext';
import { auditService } from '../../services/auditService';
import { AuditLogEntry } from '../../types/audit';

interface ConfigChainPanelProps {
  /** SHA-256 hex digest of the device's current assembled config.
   *  `null` means the device has never been polled through the
   *  cached-assembly path — the backend will populate this on the
   *  first GET /v2/conf/{id}. */
  configHash?: string | null;
  /** Wall-clock ISO-8601 timestamp of when `configHash` was produced. */
  configGeneratedAt?: string | null;
  /** When the last `device.config.regenerated` audit row was written
   *  for this device. Driven by the 60s debounce window on the
   *  backend — if the config is flapping faster than 60s, this lags
   *  `configGeneratedAt`. */
  lastConfigAuditAt?: string | null;
  /** The device ID, used to fetch the recent regeneration history. */
  deviceId: string;
}

/**
 * Shows the device's config-chain cache metadata (hash, generation
 * time, last audit) plus the last few `device.config.regenerated`
 * audit rows. Lives alongside `ConfigSourcesPanel` on the Settings
 * tab so admins can see both "what's currently merged into the
 * config" and "when did the config actually change".
 *
 * Renders nothing when the device has never been polled (all three
 * metadata fields are null/undefined) AND there are no audit rows
 * to show — safe to mount against older devices that pre-date the
 * caching layer.
 */
const ConfigChainPanel: React.FC<ConfigChainPanelProps> = ({
  configHash,
  configGeneratedAt,
  lastConfigAuditAt,
  deviceId,
}) => {
  const { t, formatDate, formatRelativeTime } = useI18n();
  const [regenHistory, setRegenHistory] = useState<AuditLogEntry[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [fullHashVisible, setFullHashVisible] = useState(false);
  const [copied, setCopied] = useState(false);

  const loadHistory = useCallback(async () => {
    if (!deviceId) return;
    setLoadingHistory(true);
    setHistoryError(null);
    try {
      const response = await auditService.listAuditLog({
        resource_type: 'device',
        resource_id: deviceId,
        action: 'device.config.regenerated',
        page: 1,
        page_size: 10,
      });
      setRegenHistory(response.items || []);
    } catch (err) {
      console.error('[ConfigChainPanel] load failed:', err);
      setHistoryError(
        err instanceof Error ? err.message : 'Failed to load regeneration history'
      );
    } finally {
      setLoadingHistory(false);
    }
  }, [deviceId]);

  // Load the first page of regeneration history only when the user
  // expands the section — most admin visits don't need it and
  // skipping the fetch saves a round-trip per Settings-tab open.
  useEffect(() => {
    if (expanded && regenHistory.length === 0 && !loadingHistory && !historyError) {
      loadHistory();
    }
  }, [expanded, regenHistory.length, loadingHistory, historyError, loadHistory]);

  const hasMetadata = !!(configHash || configGeneratedAt || lastConfigAuditAt);

  // Nothing to show if the device is too old (no metadata) AND we
  // haven't loaded any regen history.
  if (!hasMetadata && regenHistory.length === 0 && !loadingHistory) {
    return null;
  }

  const shortHash = configHash ? configHash.slice(0, 12) : null;

  const copyHash = () => {
    if (!configHash) return;
    navigator.clipboard.writeText(configHash).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    });
  };

  const extractRegenHashes = (
    entry: AuditLogEntry,
  ): { oldHash: string | null; newHash: string | null } => {
    const old = entry.old_value as Record<string, unknown> | null | undefined;
    const neu = entry.new_value as Record<string, unknown> | null | undefined;
    return {
      oldHash: (old?.config_hash as string) || null,
      newHash: (neu?.config_hash as string) || null,
    };
  };

  return (
    <div className="config-chain-panel">
      <div className="config-chain-header">
        <h3>{t('configChain.title')}</h3>
        <p className="config-chain-description">{t('configChain.description')}</p>
      </div>

      {hasMetadata && (
        <div className="config-chain-metadata">
          <div className="config-chain-row">
            <span className="config-chain-label">{t('configChain.currentHash')}:</span>
            {configHash ? (
              <span className="config-chain-hash" title={configHash}>
                <code className="config-chain-hash-value">
                  {fullHashVisible ? configHash : `${shortHash}…`}
                </code>
                <button
                  type="button"
                  className="config-chain-hash-btn"
                  onClick={() => setFullHashVisible((v) => !v)}
                >
                  {fullHashVisible ? t('configChain.hide') : t('configChain.show')}
                </button>
                <button
                  type="button"
                  className="config-chain-hash-btn"
                  onClick={copyHash}
                  title={t('configChain.copyHash')}
                >
                  {copied ? t('configChain.copied') : t('configChain.copy')}
                </button>
              </span>
            ) : (
              <span className="config-chain-null">{t('configChain.neverPolled')}</span>
            )}
          </div>

          <div className="config-chain-row">
            <span className="config-chain-label">{t('configChain.generatedAt')}:</span>
            {configGeneratedAt ? (
              <span title={formatDate(configGeneratedAt)}>
                {formatRelativeTime(configGeneratedAt)}
              </span>
            ) : (
              <span className="config-chain-null">—</span>
            )}
          </div>

          <div className="config-chain-row">
            <span className="config-chain-label">{t('configChain.lastAuditAt')}:</span>
            {lastConfigAuditAt ? (
              <span title={formatDate(lastConfigAuditAt)}>
                {formatRelativeTime(lastConfigAuditAt)}
              </span>
            ) : (
              <span className="config-chain-null">{t('configChain.never')}</span>
            )}
          </div>
        </div>
      )}

      <div className="config-chain-history">
        <button
          type="button"
          className="config-chain-history-toggle"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
        >
          {expanded ? '▼' : '▶'} {t('configChain.recentChanges')}
        </button>

        {expanded && (
          <div className="config-chain-history-body">
            {loadingHistory && (
              <div className="config-chain-history-loading">
                {t('configChain.historyLoading')}
              </div>
            )}
            {historyError && (
              <div className="config-chain-history-error">{historyError}</div>
            )}
            {!loadingHistory && !historyError && regenHistory.length === 0 && (
              <div className="config-chain-history-empty">
                {t('configChain.historyEmpty')}
              </div>
            )}
            {!loadingHistory && regenHistory.length > 0 && (
              <ul className="config-chain-history-list">
                {regenHistory.map((entry) => {
                  const { oldHash, newHash } = extractRegenHashes(entry);
                  return (
                    <li key={entry.id} className="config-chain-history-row">
                      <div className="config-chain-history-when">
                        <span title={formatDate(entry.timestamp)}>
                          {formatRelativeTime(entry.timestamp)}
                        </span>
                        {entry.actor_username && (
                          <span className="config-chain-history-actor">
                            · {entry.actor_username}
                          </span>
                        )}
                      </div>
                      <div className="config-chain-history-hashes">
                        <code className="config-chain-history-hash-old" title={oldHash || '∅'}>
                          {oldHash ? oldHash.slice(0, 10) : '∅'}
                        </code>
                        <span aria-hidden="true">→</span>
                        <code className="config-chain-history-hash-new" title={newHash || '∅'}>
                          {newHash ? newHash.slice(0, 10) : '∅'}
                        </code>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConfigChainPanel;
